import React, { useEffect, useMemo, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Plus, Trash2, AlertCircle } from 'lucide-react';
import { pesticideProducts } from '../../lib/api';

// What the technician applied on this visit, as rows rather than a sentence.
//
// This used to be one free-text box called "Chemical Used (EPA)". Typed prose
// cannot be added up, so the monthly CA DPR summary had nothing to total: it
// needs the product, its EPA registration number and how much went down, per
// application. Picking from the catalog is also what keeps the registration
// number right — it is copied from the catalog row, never typed at the tailgate.

const UNITS = ['fl oz', 'oz', 'gal', 'qt', 'pt', 'lb', 'g', 'ml', 'L'];

export const emptyLine = () => ({
  key: Math.random().toString(36).slice(2),
  productId: '', productName: '', epaRegNo: '', activeIngredient: '',
  amount: '', unit: 'fl oz', site: '', method: '', targetPest: '',
});

// A row is usable when it names a product and a quantity. Everything else is
// detail the office can fill in; without these two there is nothing to report.
export function lineIsComplete(l) {
  return Boolean((l.productName || '').trim()) && Number(l.amount) > 0;
}

export function toApplications(lines) {
  return lines.filter(lineIsComplete).map((l) => ({
    productId: l.productId || null,
    productName: l.productName.trim(),
    epaRegNo: l.epaRegNo || '',
    activeIngredient: l.activeIngredient || '',
    amount: Number(l.amount),
    unit: l.unit || 'fl oz',
    site: l.site || 'Structural',
    method: l.method || '',
    targetPest: l.targetPest || '',
  }));
}

export default function PesticideLog({ lines, onChange, noneUsed, onNoneUsed, vocab }) {
  const [catalog, setCatalog] = useState([]);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    pesticideProducts.list()
      .then((r) => setCatalog(Array.isArray(r) ? r : []))
      .catch(() => setFailed(true));
  }, []);

  const byId = useMemo(() => {
    const m = {};
    catalog.forEach((p) => { m[p.id] = p; });
    return m;
  }, [catalog]);

  const set = (key, patch) => onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  const pickProduct = (key, id) => {
    const p = byId[id];
    set(key, p
      ? { productId: p.id, productName: p.name || '', epaRegNo: p.epaRegNo || '', activeIngredient: p.activeIngredient || '' }
      : { productId: '', epaRegNo: '', activeIngredient: '' });
  };

  const sites = vocab?.site || [];
  const methods = vocab?.method || [];

  return (
    <div className="space-y-3" data-testid="pesticide-log">
      <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 bg-white">
        <input
          type="checkbox"
          checked={noneUsed}
          onChange={(e) => onNoneUsed(e.target.checked)}
          className="mt-0.5 w-5 h-5 accent-[#8BC53F]"
          data-testid="no-pesticide-used"
        />
        <span className="text-sm text-slate-700">
          <span className="font-semibold">No pesticide used on this visit</span>
          <span className="block text-xs text-slate-500">Inspection, monitoring or exclusion only. Tick this so the visit has a record either way.</span>
        </span>
      </label>

      {!noneUsed && (
        <>
          {failed && (
            <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2.5">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>The product list did not load. You can still type the product name — check the EPA number in the office.</span>
            </div>
          )}
          {lines.map((l, i) => (
            <div key={l.key} className="p-3 rounded-lg border border-slate-200 bg-white space-y-2.5" data-testid={`pesticide-line-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wide">Product {i + 1}</span>
                {lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onChange(lines.filter((x) => x.key !== l.key))}
                    className="w-11 h-11 -mr-2 flex items-center justify-center text-slate-400 hover:text-red-600"
                    aria-label={`Remove product ${i + 1}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <select
                value={l.productId}
                onChange={(e) => pickProduct(l.key, e.target.value)}
                className="w-full h-11 px-3 text-sm border border-slate-200 rounded-md bg-white"
                data-testid={`pesticide-product-${i}`}
              >
                <option value="">Select a product…</option>
                {catalog.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>

              {!l.productId && (
                <Input
                  value={l.productName}
                  onChange={(e) => set(l.key, { productName: e.target.value })}
                  placeholder="…or type the product name"
                  className="h-11"
                />
              )}

              {l.epaRegNo && (
                <div className="text-xs text-slate-500">
                  EPA Reg. <span className="font-mono text-slate-700">{l.epaRegNo}</span>
                  {l.activeIngredient ? <> · {l.activeIngredient}</> : null}
                </div>
              )}

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Amount</label>
                  <Input
                    type="number" inputMode="decimal" min="0" step="any"
                    value={l.amount}
                    onChange={(e) => set(l.key, { amount: e.target.value })}
                    className="mt-1 h-11"
                    data-testid={`pesticide-amount-${i}`}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Unit</label>
                  <select
                    value={l.unit}
                    onChange={(e) => set(l.key, { unit: e.target.value })}
                    className="mt-1 w-full h-11 px-3 text-sm border border-slate-200 rounded-md bg-white"
                    data-testid={`pesticide-unit-${i}`}
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs font-semibold text-slate-700">Where</label>
                  <select
                    value={l.site}
                    onChange={(e) => set(l.key, { site: e.target.value })}
                    className="mt-1 w-full h-11 px-3 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="">—</option>
                    {sites.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">How</label>
                  <select
                    value={l.method}
                    onChange={(e) => set(l.key, { method: e.target.value })}
                    className="mt-1 w-full h-11 px-3 text-sm border border-slate-200 rounded-md bg-white"
                  >
                    <option value="">—</option>
                    {methods.map((x) => <option key={x} value={x}>{x}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}

          <Button
            type="button" variant="outline"
            onClick={() => onChange([...lines, emptyLine()])}
            className="w-full h-11 gap-1.5"
            data-testid="add-pesticide-line"
          >
            <Plus className="w-4 h-4" />Add another product
          </Button>
        </>
      )}
    </div>
  );
}
